import { automationsDisplayName, Pegasus } from 'corso-types';

// ! these routes and card hashes are also linked from packages/corso-lib/generateCrewFrontEndUrl.ts (merchantSettingsSections);
// ! update it when moving a settings page or renaming a card id, or those links break silently
export type SettingsSection = {
  readonly label: string;
  readonly hash: string;
  readonly keywords?: readonly string[];
};

export type SettingsSearchEntry = {
  readonly to: string;
  readonly sections: readonly SettingsSection[];
  readonly keywords?: readonly string[];
};

const automationsEntry: SettingsSearchEntry = {
  to: 'automations',
  keywords: ['automation', 'rules', 'store rules', 'flow'],
  sections: Pegasus.returnOrWarrantyHook.map((hook) => ({
    label: automationsDisplayName.hook[hook],
    hash: hook,
    keywords: Pegasus.eventsForHook[hook]
      .filter((et) => !Pegasus.hiddenEvents.includes(et))
      .map((et) => automationsDisplayName.eventType[et]),
  })),
};

/**
 * Static search config mapping each settings route to its searchable subsections.
 * This is the single source of truth for section anchors — use `SettingsAnchor`
 * to type-check `id` props on Card/heading components in settings pages.
 */
const staticEntries = [
  {
    to: 'returns',
    sections: [
      {
        label: 'Return Policy & Media',
        hash: 'general',
        keywords: ['policy url', 'media upload', 'require image'],
      },
      {
        label: 'Return Inspections',
        hash: 'general',
        keywords: ['inspection'],
      },
      { label: 'Gift Returns', hash: 'general', keywords: ['gift return'] },
      {
        label: 'Return Expiration',
        hash: 'general',
        keywords: ['expiration days', 'label', 'packing slip'],
      },
      {
        label: 'Order Prefixes & Discount Codes',
        hash: 'e-commerce-platform',
        keywords: ['prefix', 'discount code', 'replacement order'],
      },
      {
        label: 'Refunds',
        hash: 'refunds',
        keywords: ['refund window', 'refund offered'],
      },
      {
        label: 'Store Credit',
        hash: 'store-credit',
        keywords: ['gift card', 'incentive', 'store credit offered'],
      },
      {
        label: 'Exchanges',
        hash: 'exchanges',
        keywords: ['exchange window', 'exchange offered', 'variant exchange'],
      },
      {
        label: 'Shop Now',
        hash: 'shop-now',
        keywords: ['shop now', 'checkout'],
      },
      {
        label: 'Product Exchange Rules',
        hash: 'product-exchanges',
        keywords: ['advanced exchange', 'product exchange'],
      },
    ],
  },
  {
    to: 'email',
    sections: [
      {
        label: 'Email Sender & Recipient',
        hash: 'email-recipient-and-sender',
        keywords: ['from', 'reply to'],
      },
      {
        label: 'Automated Emails',
        hash: 'automated-emails',
        keywords: ['reminder', 'expiring', 'submitted'],
      },
      {
        label: 'Finalization Emails',
        hash: 'claim-finalization-emails',
        keywords: ['finalization'],
      },
    ],
  },
  {
    to: 'registrations',
    keywords: ['warranty claims', 'channels', 'portal'],
    sections: [
      {
        label: 'Registration Data Collection',
        hash: 'data-collection',
        keywords: [
          'proof of purchase',
          'marketing consent',
          'address',
          'phone',
        ],
      },
      { label: 'Registration Custom Fields', hash: 'custom-fields' },
      {
        label: 'Registration Text Customization',
        hash: 'text-customization',
        keywords: ['placeholder', 'submission'],
      },
    ],
  },
  {
    to: 'reasons',
    sections: [
      {
        label: 'Reason Groups',
        hash: 'reason-groups',
        keywords: ['group', 'product group'],
      },
      {
        label: 'Edit Reasons',
        hash: 'reasons',
        keywords: ['add reason', 'remove reason'],
      },
    ],
  },
  {
    to: 'tracking',
    keywords: ['tracking', 'shipment', 'delivery', 'carrier'],
    sections: [
      {
        label: 'Tracking',
        hash: 'tracking',
        keywords: [
          'tracking source',
          'tracking event provider',
          'carrier',
          'fulfillment events',
          'estimated delivery',
        ],
      },
    ],
  },
  {
    to: 'theme',
    sections: [
      {
        label: 'Customer Portal URL',
        hash: 'customer-portal-url',
        keywords: ['portal', 'embed'],
      },
      {
        label: 'Theme Branding',
        hash: 'branding',
        keywords: ['color', 'logo', 'favicon', 'background', 'css', 'font'],
      },
      {
        label: 'Order Lookup Text',
        hash: 'order-lookup',
        keywords: ['page title', 'gift return'],
      },
      { label: 'Order Hub', hash: 'order-hub', keywords: ['phone'] },
      {
        label: 'Claim Overview Page',
        hash: 'overview',
        keywords: ['label download', 'qr code', 'manual review'],
      },
      {
        label: 'Warranty Customization',
        hash: 'warranty-customization',
        keywords: ['warranty program name'],
      },
    ],
  },
  {
    to: 'shipping-plus',
    sections: [
      { label: 'Shipping Rates', hash: 'shipping-rates', keywords: ['rates'] },
      {
        label: 'Shipping Checkout Extension',
        hash: 'checkout-extension',
        keywords: ['free shipping', 'cart attributes'],
      },
    ],
  },
  {
    to: 'checkout-plus',
    sections: [
      {
        label: 'Checkout Plus Elements',
        hash: 'checkout-plus-elements',
        keywords: ['widget', 'elements'],
      },
      {
        label: 'Checkout Plus Display Countries',
        hash: 'display-countries',
        keywords: ['country'],
      },
      {
        label: 'Checkout Button',
        hash: 'checkout-button',
        keywords: ['cart', 'selectors'],
      },
      {
        label: 'Checkout Plus Custom CSS',
        hash: 'custom-css',
        keywords: ['css', 'styles'],
      },
    ],
  },
  {
    to: 'integrations',
    sections: [
      {
        label: 'E-Commerce Platform',
        hash: 'e-commerce-platform',
        keywords: ['shopify', 'subscription', 'app status'],
      },
      {
        label: 'Shipping Provider',
        hash: 'shipping-provider',
        keywords: [
          'vesyl',
          'easypost',
          'happy returns',
          'return bar',
          'shipping labels',
          'carrier',
        ],
      },
      {
        label: 'WMS Platform',
        hash: 'wms-platform',
        keywords: [
          'shiphero',
          'shipbob',
          'bluebox',
          'xb-fulfillment',
          'logiwa',
          'two boxes',
          'masonhub',
          'warehouse',
          'fulfillment',
        ],
      },
      {
        label: 'Customer Experience',
        hash: 'customer-experience',
        keywords: [
          'gorgias',
          'gladly',
          'zowie',
          'kustomer',
          'zendesk',
          'helpdesk',
          'support',
          'ticketing',
        ],
      },
      {
        label: 'Email & SMS Providers',
        hash: 'email-sms-providers',
        keywords: [
          'klaviyo',
          'sendlane',
          'attentive',
          'postscript',
          'marketing',
          'notifications',
        ],
      },
      {
        label: 'Subscription Management',
        hash: 'subscription-management',
        keywords: ['skio', 'recharge', 'recurring', 'subscriptions'],
      },
      {
        label: 'Cross-Border',
        hash: 'cross-border',
        keywords: ['global-e', 'global e', 'cross border', 'international'],
      },
      {
        label: 'Other Integrations',
        hash: 'other-integrations',
        keywords: [
          'stripe',
          'localize',
          'brij',
          'intelligems',
          'translation',
          'payments',
        ],
      },
    ],
  },
  {
    to: 'developer',
    sections: [
      {
        label: 'Webhooks',
        hash: 'webhooks',
        keywords: ['webhook', 'endpoint', 'event', 'callback', 'http'],
      },
      {
        label: 'Commerce API',
        hash: 'commerce-api',
        keywords: ['api key', 'public api', 'token', 'credentials', 'rest'],
      },
      {
        label: 'MCP Server',
        hash: 'mcp-server',
        keywords: ['mcp', 'claude', 'chatgpt', 'assistant', 'connector', 'ai'],
      },
    ],
  },
] as const satisfies readonly SettingsSearchEntry[];

/** Union of every section anchor across all routes. */
export type SettingsAnchor =
  (typeof staticEntries)[number]['sections'][number]['hash'];

export const settingsSearchConfig: readonly SettingsSearchEntry[] = [
  automationsEntry,
  ...staticEntries,
];
